import React, { useCallback, useMemo, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Alert,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridOverlay } from '@mui/x-data-grid-pro';

import {
  DEFAULT_TERMS_AND_CONDITIONS_BRAND,
  DEFAULT_TERMS_AND_CONDITIONS_LOCALE,
  TERMS_AND_CONDITIONS_BRANDS,
  TermsAndConditionsBrand,
  termsAndConditionsBrandLabel,
} from '~/consts/termsAndConditions';

import { useGetTermsAndConditionsList } from '~/hooks/apis/content';
import { useGetLanguageOptions } from '~/hooks/apis/lab/useGetLanguageOptions';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { TermsAndConditionsSummary } from '~/services/ContentService';

import TermsAndConditionsEditorDialog from './TermsAndConditionsEditorDialog';

const EMPTY_GRID_MIN_HEIGHT = 280;
const MIN_TRANSLATIONS_COLUMN_WIDTH = 200;
/** Chip width plus the surrounding stack spacing. */
const TRANSLATION_CHIP_WIDTH = 74;

interface EditorTarget {
  summary: TermsAndConditionsSummary | null;
  locale: string;
}

export default function TermsAndConditionsPage() {
  const [brand, setBrand] = useState<TermsAndConditionsBrand>(DEFAULT_TERMS_AND_CONDITIONS_BRAND);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);

  const { tenant } = useCurrentTenant();
  const [brandWarningDismissedFor, setBrandWarningDismissedFor] = useState<string | null>(null);
  const isBrandMismatched = tenant.brand !== brand;

  const { data: documents = [], isLoading, isError } = useGetTermsAndConditionsList(brand);
  const { data: locales = [] } = useGetLanguageOptions();

  const openEditor = useCallback((summary: TermsAndConditionsSummary | null, locale: string) => {
    setEditorTarget({ summary, locale });
  }, []);

  const existingSlugs = useMemo(() => documents.map((document) => document.slug), [documents]);

  const brandLabel = termsAndConditionsBrandLabel(brand);

  const renderNoRowsOverlay = useCallback(
    () => (
      <GridOverlay>
        <Stack alignItems="center" spacing={1} sx={{ maxWidth: 420, px: 3, textAlign: 'center' }}>
          <DescriptionOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography variant="subtitle1">No documents for {brandLabel}</Typography>
          <Typography color="text.secondary" variant="body2">
            Create one, or import it from Contentful.
          </Typography>
        </Stack>
      </GridOverlay>
    ),
    [brandLabel],
  );

  const columns: Array<GridColDef<TermsAndConditionsSummary>> = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Document',
        flex: 1,
        minWidth: 260,
        renderCell: ({ row }) => (
          <Stack justifyContent="center" height="100%" spacing={0.25}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {row.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace" noWrap>
              {row.slug}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'locales',
        headerName: 'Translations',
        sortable: false,
        width: Math.max(MIN_TRANSLATIONS_COLUMN_WIDTH, locales.length * TRANSLATION_CHIP_WIDTH),
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1.25} alignItems="center" height="100%">
            {locales.map((locale) => {
              const exists = row.locales.includes(locale.value);
              return (
                <Tooltip
                  key={locale.value}
                  title={exists ? `Edit the ${locale.value} version` : `Create a ${locale.value} version`}
                >
                  <Chip
                    label={locale.value}
                    size="small"
                    color={exists ? 'primary' : 'default'}
                    variant={exists ? 'filled' : 'outlined'}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEditor(row, locale.value);
                    }}
                    sx={{
                      width: 64,
                      fontSize: 11,
                      ...(exists ? {} : { color: 'text.disabled', borderStyle: 'dashed' }),
                    }}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        width: 90,
        renderCell: ({ row }) => (
          <Stack height="100%" justifyContent="center" alignItems="flex-end">
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openEditor(row, DEFAULT_TERMS_AND_CONDITIONS_LOCALE);
              }}
            >
              Edit
            </Button>
          </Stack>
        ),
      },
    ],
    [locales, openEditor],
  );

  return (
    <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, md: 3, lg: 4 }, pt: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <TextField
            select
            size="small"
            label="Brand"
            value={brand}
            onChange={(event) => setBrand(event.target.value as TermsAndConditionsBrand)}
            sx={{ minWidth: 260 }}
          >
            {TERMS_AND_CONDITIONS_BRANDS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Button variant="contained" onClick={() => openEditor(null, DEFAULT_TERMS_AND_CONDITIONS_LOCALE)}>
            Create document
          </Button>
        </Stack>

        {isError && <Alert severity="error">Failed to load terms and conditions. Please try again.</Alert>}

        <DataGridPro
          rows={documents}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.slug}
          rowHeight={56}
          columnHeaderHeight={44}
          autoHeight
          hideFooter
          initialState={{
            sorting: { sortModel: [{ field: 'title', sort: 'asc' }] },
          }}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={({ row }) => openEditor(row, DEFAULT_TERMS_AND_CONDITIONS_LOCALE)}
          slots={{ noRowsOverlay: renderNoRowsOverlay }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          sx={{
            minHeight: documents.length === 0 ? EMPTY_GRID_MIN_HEIGHT : undefined,
            bgcolor: 'background.paper',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50' },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
        />
      </Stack>

      <Dialog
        open={isBrandMismatched && brandWarningDismissedFor !== tenant.brand}
        onClose={() => setBrandWarningDismissedFor(tenant.brand)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>You are editing {brandLabel}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2">
              Anything you change here applies to the terms and conditions for <strong>{brandLabel}</strong>, not{' '}
              <strong>{tenant.title}</strong>.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setBrandWarningDismissedFor(tenant.brand)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {editorTarget && (
        <TermsAndConditionsEditorDialog
          onClose={() => setEditorTarget(null)}
          brand={brand}
          summary={editorTarget.summary}
          initialLocale={editorTarget.locale}
          existingSlugs={existingSlugs}
        />
      )}
    </Container>
  );
}
